import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Eye, EyeOff, Loader2, ArrowRight } from "@/components/ds/icons";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { useUser } from "@/contexts/UserContext";

interface MostrarResponse {
  email: string;
  nombre: string;
  empresa_nombre: string | null;
  invitador_nombre: string | null;
}

interface ConfirmarResponse {
  access_token: string;
}

export function ConfirmarMandante() {
  const [, navigate] = useLocation();
  const { refreshUser } = useUser();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const userId = params.get("user");
  const signedQuery = useMemo(() => buildSignedQuery(params), [params]);

  const [isLoading, setIsLoading] = useState(true);
  const [info, setInfo] = useState<MostrarResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!userId || !signedQuery) {
      setErrorMsg("El enlace es inválido o está incompleto.");
      setIsLoading(false);
      return;
    }

    api
      .get<MostrarResponse>(`/auth/mandantes/confirmar/${userId}${signedQuery}`)
      .then((res) => setInfo(res))
      .catch((err) => {
        if (err instanceof ApiError && err.status === 410) {
          setErrorMsg("Esta cuenta ya fue confirmada. Inicia sesión normalmente.");
        } else if (err instanceof ApiError && err.status === 403) {
          setErrorMsg("El enlace expiró o no es válido.");
        } else {
          setErrorMsg("No se pudo cargar la invitación.");
        }
      })
      .finally(() => setIsLoading(false));
  }, [userId, signedQuery]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (password !== passwordConfirmation) {
      toast.error("Las contraseñas no coinciden.");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await api.post<ConfirmarResponse>(
        `/auth/mandantes/confirmar/${userId}${signedQuery}`,
        { password, password_confirmation: passwordConfirmation },
      );
      sessionStorage.setItem("auth_token", res.access_token);
      await refreshUser();
      toast.success("¡Cuenta activada!");
      navigate("/");
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && err.errors) {
        const firstError = Object.values(err.errors)[0]?.[0];
        toast.error(firstError ?? "Revisa los datos.");
      } else {
        toast.error("No se pudo confirmar la cuenta.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground mx-auto mb-4">
            <FileText className="h-6 w-6" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Facil Control</h1>
          <p className="text-muted-foreground mt-2">Confirma tu registro</p>
        </div>

        <Card className="border-0 shadow-lg">
          {isLoading ? (
            <CardContent className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </CardContent>
          ) : errorMsg ? (
            <CardContent className="py-12 text-center space-y-4">
              <p className="text-sm text-muted-foreground">{errorMsg}</p>
              <Button variant="outline" onClick={() => navigate("/login")}>
                Ir al login
              </Button>
            </CardContent>
          ) : info ? (
            <>
              <CardHeader className="space-y-2 pb-6">
                <CardTitle className="text-2xl">Hola {info.nombre}</CardTitle>
                <CardDescription>
                  {info.invitador_nombre ? (
                    <>
                      <strong>{info.invitador_nombre}</strong> te invitó a registrar a{" "}
                      <strong>{info.empresa_nombre}</strong> como mandante. Vas a poder auditar
                      sus contratos y documentos. Define una contraseña para activar tu cuenta.
                    </>
                  ) : (
                    <>Define una contraseña para activar tu cuenta de {info.empresa_nombre}.</>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Email</Label>
                    <Input value={info.email} disabled />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Contraseña</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-11 pr-10"
                        autoComplete="new-password"
                        disabled={isSubmitting}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">Mínimo 8 caracteres</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password-confirmation">Confirmar contraseña</Label>
                    <Input
                      id="password-confirmation"
                      type={showPassword ? "text" : "password"}
                      value={passwordConfirmation}
                      onChange={(e) => setPasswordConfirmation(e.target.value)}
                      className="h-11"
                      autoComplete="new-password"
                      disabled={isSubmitting}
                    />
                  </div>

                  <Button type="submit" disabled={isSubmitting} className="w-full h-11 gap-2 mt-6">
                    {isSubmitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        Activar cuenta
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </>
          ) : null}
        </Card>
      </div>
    </div>
  );
}

function buildSignedQuery(params: URLSearchParams): string {
  const expires = params.get("expires");
  const signature = params.get("signature");
  if (!expires || !signature) return "";
  const sp = new URLSearchParams();
  sp.set("expires", expires);
  sp.set("signature", signature);
  return `?${sp.toString()}`;
}
